import { useEffect } from "react";
import { BRAND } from "./brandConfig";

/**
 * Injects JSON-LD structured data into <head>.
 * `id` becomes the script element's id so we can safely replace on rerender
 * and remove on unmount — preventing schema duplication across SPA navigations.
 */
export const useJsonLd = (id, data) => {
  const json = data ? JSON.stringify(data) : "";
  useEffect(() => {
    if (!json || typeof document === "undefined") return undefined;
    const elId = `jsonld-${id}`;
    let el = document.getElementById(elId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = elId;
      document.head.appendChild(el);
    }
    el.textContent = json;
    return () => {
      const stale = document.getElementById(elId);
      if (stale) stale.remove();
    };
  }, [id, json]);
};

/** Build the LocalBusiness schema for a neighborhood page. */
export const buildLocalBusinessSchema = ({ name, slug, geo, intro }) => ({
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "name": `Urban Nest Quest — ${name} Apartment Locator`,
  "description": intro,
  "url": `https://urbannestquest.com/apartments/${slug}`,
  "telephone": "+1-936-224-1484",
  "email": "Bryan@UrbanNestQuest.com",
  "areaServed": { "@type": "Place", "name": `${name}, Houston, TX` },
  "address": { "@type": "PostalAddress", "addressLocality": "Houston", "addressRegion": "TX", "addressCountry": "US" },
  "geo": geo ? { "@type": "GeoCoordinates", "latitude": geo.lat, "longitude": geo.lng } : undefined,
  "parentOrganization": { "@type": "Organization", "name": BRAND.brokerage },
  "priceRange": "Free service for renters",
});

export const buildBreadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": crumbs.map((c, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": c.name,
    "item": c.url,
  })),
});

export const buildFaqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": (faqs || []).map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
});

/** Article / BlogPosting schema for blog content. */
export const buildArticleSchema = ({ title, description, slug, publishedAt, keywords }) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": title,
  "description": description,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://urbannestquest.com/blog/${slug}`,
  },
  "author": {
    "@type": "Organization",
    "name": "Urban Nest Quest",
    "url": "https://urbannestquest.com/",
  },
  "publisher": {
    "@type": "Organization",
    "name": "Urban Nest Quest",
    "url": "https://urbannestquest.com/",
  },
  "datePublished": publishedAt,
  "dateModified": publishedAt,
  "keywords": (keywords || []).join(", "),
});
